import { API_URL, fetchWithHandling } from './api';

// ---------------------------------------------------------------------------
// Report Template API Functions
// ---------------------------------------------------------------------------

/**
 * List all report templates for the authenticated user.
 */
export async function listReportTemplates(accessToken) {
  return fetchWithHandling(`${API_URL}/data/report-template/list`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Suggest report templates for chat mention based on name.
 */
export async function getReportTemplateSuggestions(
  accessToken,
  query,
  limit = 10,
) {
  return fetchWithHandling(
    `${API_URL}/data/report-template/suggestions?query=${encodeURIComponent(query)}&limit=${limit}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

/**
 * Get full details of a specific report template.
 */
export async function getReportTemplate(accessToken, templateId) {
  return fetchWithHandling(`${API_URL}/data/report-template/${templateId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Delete a report template.
 */
export async function deleteReportTemplate(accessToken, templateId) {
  return fetchWithHandling(
    `${API_URL}/data/report-template/delete/${templateId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

/**
 * Toggle active/inactive status of a report template.
 */
export async function toggleReportTemplateStatus(accessToken, templateId) {
  return fetchWithHandling(
    `${API_URL}/data/report-template/toggle-status/${templateId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

/**
 * Send a message to the Template Builder agent.
 * Uses the same chat endpoint as the main chat, but with agent_name = "TemplateBuilder".
 */
export async function sendTemplateBuilderMessage(accessToken, message, taskId) {
  return fetchWithHandling(`${API_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      prompt: message,
      task_id: taskId,
      agent_name: 'TemplateBuilder',
    }),
  });
}

/**
 * Update a single section in a report template.
 */
export async function updateTemplateSection(
  accessToken,
  templateId,
  sectionIndex,
  sectionData,
) {
  return fetchWithHandling(
    `${API_URL}/data/report-template/update-section/${templateId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        section_index: sectionIndex,
        section: sectionData,
      }),
    },
  );
}

/**
 * Insert a new section at a specific index in a report template.
 */
export async function insertTemplateSection(
  accessToken,
  templateId,
  insertAtIndex,
  sectionData,
) {
  return fetchWithHandling(
    `${API_URL}/data/report-template/insert-section/${templateId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        insert_at_index: insertAtIndex,
        section: sectionData,
      }),
    },
  );
}

/**
 * Auto-parse a .docx file and save it as a report template (no LLM agent needed).
 * Sends the file directly to the backend which parses structure and saves to DB.
 */
export async function autoParseTemplate(accessToken, file) {
  const formData = new FormData();
  formData.append('file', file);

  return fetchWithHandling(`${API_URL}/data/report-template/auto-parse`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
}
